import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Select,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Typography,
  Popconfirm,
  Tag,
  Card,
  Switch,
  Divider,
  Alert,
  Tooltip,
  message,
  notification,
  Spin,
} from 'antd';
import { PlusOutlined, MinusCircleOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  getSecurityGroups,
  getSGRules,
  addSGRule,
  updateSGRuleDescription,
  deleteSGRule,
} from '../api/client';
import { accountLabel } from '../utils/accountNames';

const { Title, Text } = Typography;
const { Option } = Select;

// ─── Protocol options for the Add Rule form ───────────────────────────────────

// AWS max inbound rules per SG (raised from default 60 to 125 for this account)
const MAX_SG_RULES = 125;

// Returns today as YYYYMMDD for the description template
const todayYYYYMMDD = () => new Date().toISOString().slice(0, 10).replace(/-/g, '');

const PROTOCOL_OPTIONS = [
  { label: 'TCP', value: 'tcp' },
  { label: 'UDP', value: 'udp' },
  { label: 'ICMP', value: 'icmp' },
  { label: 'All Traffic (-1)', value: '-1' },
];

// ─── CIDR validation helper ───────────────────────────────────────────────────
//
// Returns true if the string is a valid IPv4 CIDR block.
// Examples: "203.0.113.5/32", "10.0.0.0/8"
//
const isValidCIDR = (cidr) => {
  if (!cidr) return false;
  const parts = cidr.split('/');
  if (parts.length !== 2) return false;

  const [ip, prefix] = parts;
  const octets = ip.split('.');
  if (octets.length !== 4) return false;

  const allOctetsValid = octets.every((o) => {
    const n = Number(o);
    return Number.isInteger(n) && n >= 0 && n <= 255 && o !== '';
  });
  const prefixNum = Number(prefix);
  const prefixValid = Number.isInteger(prefixNum) && prefixNum >= 0 && prefixNum <= 32;

  return allOctetsValid && prefixValid;
};

// ─── SecurityGroups Component ─────────────────────────────────────────────────

const SecurityGroups = () => {
  // Pre-select an SG when arriving via ?sg=<sg_id> from IP Search results.
  const initialSG = new URLSearchParams(window.location.search).get('sg');

  // ── SG list (the dropdown) ──────────────────────────────────────────────────
  const [sgList, setSgList] = useState([]);
  const [sgListLoading, setSgListLoading] = useState(false);
  const [selectedSG, setSelectedSG] = useState(initialSG || null);

  // ── Rules for the selected SG ───────────────────────────────────────────────
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [searchText, setSearchText] = useState(''); // local filter on rules table

  // ── Add Rule modal ──────────────────────────────────────────────────────────
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addForm] = Form.useForm();

  // ── Description template (Add Rule modal) ──────────────────────────────────
  const [useTemplate, setUseTemplate] = useState(false);
  const [tmplInc, setTmplInc]         = useState('');
  const [tmplApp, setTmplApp]         = useState('');
  const [tmplUser, setTmplUser]       = useState('');

  // ── Edit Description modal ──────────────────────────────────────────────────
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [editForm] = Form.useForm();

  // ── Fetch the list of all SGs on mount ─────────────────────────────────────
  useEffect(() => {
    const fetchSGList = async () => {
      setSgListLoading(true);
      try {
        const data = await getSecurityGroups();
        // API returns { security_groups: [...] } from manage_sg Lambda.
        // Items use resource_id/name from DynamoDB — normalise to group_id/group_name.
        const rawItems = data.security_groups ?? data.resources ?? (Array.isArray(data) ? data : []);
        const sgs = rawItems
          .filter((r) => !r.resource_type || r.resource_type === 'SecurityGroup')
          .map((r) => ({
            group_id:   r.group_id   ?? r.resource_id,
            group_name: r.group_name ?? r.name ?? r.resource_id,
            account_id: r.account_id,
            region:     r.region,
            vpc_id:     r.details?.vpc_id ?? r.vpc_id,
            description: r.details?.description ?? r.description,
            rule_count:        r.rule_count ?? null,
            rule_count_source: r.details?.rule_count_source ?? null,
            rule_count_updated_at: r.details?.rule_count_updated_at ?? null,
          }));
        setSgList(sgs);
      } catch (err) {
        notification.error({ message: 'Failed to load security groups', description: err.response?.data?.message ?? err.response?.data?.error ?? err.message ?? 'Please refresh the page.', duration: 0 });
      } finally {
        setSgListLoading(false);
      }
    };
    fetchSGList();
  }, []);

  // ── Fetch rules whenever selectedSG changes ─────────────────────────────────
  //
  // useCallback ensures fetchRules doesn't change identity on every render,
  // which would cause the useEffect below to run in a loop.
  //
  const fetchRules = useCallback(async (sgId) => {
    if (!sgId) return;
    setRulesLoading(true);
    try {
      const data = await getSGRules(sgId);
      setRules(data.rules ?? data ?? []);
    } catch (err) {
      notification.error({
        message: 'Failed to load rules',
        description: err.response?.data?.message ?? err.response?.data?.error ?? err.message ?? 'Could not load rules for this security group.',
        duration: 0,
      });
    } finally {
      setRulesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules(selectedSG);
  }, [selectedSG, fetchRules]);

  // ── Add Rule ────────────────────────────────────────────────────────────────

  const handleAddRule = async (values) => {
    const cidrs = (values.cidrs ?? []).map((c) => c.trim()).filter(Boolean);
    if (!cidrs.length) return;

    const description = useTemplate
      ? `${tmplInc} - ${tmplApp} - ${tmplUser} - ${todayYYYYMMDD()}`
      : (values.description ?? '');

    const ruleBase = {
      protocol:  values.protocol,
      from_port: values.protocol === '-1' ? -1 : Number(values.from_port),
      to_port:   values.protocol === '-1' ? -1 : Number(values.to_port),
      description,
    };

    setAddLoading(true);
    try {
      const results = await Promise.allSettled(
        cidrs.map((cidr) => addSGRule(selectedSG, { ...ruleBase, cidr_ip: cidr }))
      );
      const failed  = results.filter((r) => r.status === 'rejected');
      const success = results.filter((r) => r.status === 'fulfilled');

      if (success.length) message.success(`${success.length} rule${success.length > 1 ? 's' : ''} added successfully.`);
      if (failed.length)  notification.error({ message: `${failed.length} rule${failed.length > 1 ? 's' : ''} failed to add`, description: failed[0].reason?.response?.data?.message ?? 'Check the AWS console for details.', duration: 0 });

      if (success.length) {
        setAddModalOpen(false);
        addForm.resetFields();
        setUseTemplate(false);
        setTmplInc(''); setTmplApp(''); setTmplUser('');
        fetchRules(selectedSG);
      }
    } finally {
      setAddLoading(false);
    }
  };

  // ── Edit Description ────────────────────────────────────────────────────────

  const openEditModal = (rule) => {
    setEditingRule(rule);
    editForm.setFieldsValue({ description: rule.description ?? '' });
    setEditModalOpen(true);
  };

  const handleEditDescription = async (values) => {
    setEditLoading(true);
    try {
      await updateSGRuleDescription(selectedSG, editingRule.rule_id, values.description);
      // Update local state immediately so the UI reflects the change without waiting for the reload.
      const updatedRuleId = editingRule.rule_id;
      setRules((prev) =>
        prev.map((r) => (r.rule_id === updatedRuleId ? { ...r, description: values.description } : r))
      );
      message.success('Description updated.');
      setEditModalOpen(false);
      setEditingRule(null);
      fetchRules(selectedSG);
    } catch (err) {
      notification.error({ message: 'Failed to update description', description: err.response?.data?.message ?? 'An unexpected error occurred.', duration: 0 });
    } finally {
      setEditLoading(false);
    }
  };

  // ── Delete Rule ─────────────────────────────────────────────────────────────

  const handleDeleteRule = async (ruleId) => {
    try {
      await deleteSGRule(selectedSG, ruleId);
      message.success('Rule deleted.');
      fetchRules(selectedSG);
    } catch (err) {
      notification.error({ message: 'Failed to delete rule', description: err.response?.data?.message ?? 'An unexpected error occurred.', duration: 0 });
    }
  };

  // ── Local filter on the rules table ────────────────────────────────────────

  const filteredRules = rules.filter((r) => {
    if (!searchText) return true;
    const lower = searchText.toLowerCase();
    return (
      (r.cidr_ip ?? '').toLowerCase().includes(lower) ||
      (r.description ?? '').toLowerCase().includes(lower)
    );
  });

  // Find the full SG object for the selected ID (used for display)
  const selectedSGObj = sgList.find((sg) => sg.group_id === selectedSG);

  // ── Table column definitions ────────────────────────────────────────────────

  const columns = [
    {
      title: 'CIDR / IP',
      dataIndex: 'cidr_ip',
      key: 'cidr_ip',
      render: (cidr) => <Text code>{cidr}</Text>,
    },
    {
      title: 'Protocol',
      dataIndex: 'protocol',
      key: 'protocol',
      render: (proto) => (
        <Tag color={proto === '-1' ? 'red' : 'blue'}>
          {proto === '-1' ? 'All Traffic' : (proto ?? '').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Port Range',
      key: 'port_range',
      render: (_, record) => {
        if (record.protocol === '-1') {
          return <Text type="secondary">All</Text>;
        }
        if (record.from_port === record.to_port) {
          return <Text>{record.from_port}</Text>;
        }
        return <Text>{record.from_port} – {record.to_port}</Text>;
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (desc) => desc || <Text type="secondary">—</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          >
            Edit
          </Button>

          {/*
            Popconfirm shows an inline confirmation before the delete runs.
            This is safer than a full Modal — it keeps the user in context.
          */}
          <Popconfirm
            title="Delete this rule?"
            description="This cannot be undone."
            onConfirm={() => handleDeleteRule(record.rule_id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            placement="left"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>

      {/* ── Page Header ────────────────────────────────────────────────── */}
      <Title level={4} style={{ margin: 0 }}>
        Security Group Manager
      </Title>

      {/* ── SG Selector ────────────────────────────────────────────────── */}
      <Card title="Select a Security Group" size="small">
        <Spin spinning={sgListLoading}>
          <Select
            placeholder="Search by name or ID..."
            style={{ width: '100%', maxWidth: 760 }}
            dropdownStyle={{ minWidth: 760 }}
            value={selectedSG}
            onChange={(val) => {
              setSelectedSG(val);
              setSearchText('');
              setRules([]); // Clear stale rules immediately
            }}
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(a, b) =>
              (a.rule_count ?? Infinity) - (b.rule_count ?? Infinity)
            }
            notFoundContent={sgListLoading ? <Spin size="small" /> : 'No security groups found'}
          >
            {sgList.map((sg) => (
              <Option
                key={sg.group_id}
                value={sg.group_id}
                label={`${sg.group_name} ${sg.group_id}`}
                rule_count={sg.rule_count ?? Infinity}
              >
                <Space>
                  <Text strong>{sg.group_name}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>{sg.group_id}</Text>
                  {sg.account_id && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      · {accountLabel(sg.account_id)}
                    </Text>
                  )}
                  {sg.region && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      · {sg.region}
                    </Text>
                  )}
                  {sg.rule_count != null && (
                    <Tag
                      color={sg.rule_count >= MAX_SG_RULES ? 'red' : sg.rule_count >= MAX_SG_RULES * 0.8 ? 'orange' : 'blue'}
                      style={{ fontSize: 11, marginLeft: 4 }}
                    >
                      {sg.rule_count}/{MAX_SG_RULES} rules
                    </Tag>
                  )}
                </Space>
              </Option>
            ))}
          </Select>
        </Spin>
      </Card>

      {/* ── Rules Table (rendered after an SG is selected) ──────────────── */}
      {selectedSG && (
        <Card
          title={
            <Space direction="vertical" size={0}>
              <Space>
                <Text strong>
                  Rules for: {selectedSGObj?.group_name ?? selectedSG}
                </Text>
                {selectedSGObj?.rule_count != null && (
                  <Tag
                    color={selectedSGObj.rule_count >= MAX_SG_RULES ? 'red' : selectedSGObj.rule_count >= MAX_SG_RULES * 0.8 ? 'orange' : 'blue'}
                  >
                    {selectedSGObj.rule_count}/{MAX_SG_RULES} capacity
                  </Tag>
                )}
              </Space>
              {selectedSGObj?.rule_count_source && (
                <Text type="secondary" style={{ fontSize: 11, fontWeight: 'normal' }}>
                  Count source: {selectedSGObj.rule_count_source === 'app' ? 'updated by app' : 'last infrastructure sync'}
                  {selectedSGObj.rule_count_updated_at && (
                    <> · {new Date(selectedSGObj.rule_count_updated_at).toLocaleString()}</>
                  )}
                </Text>
              )}
            </Space>
          }
          extra={
            <Space wrap>
              <Input
                placeholder="Filter by CIDR or description..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 240 }}
                allowClear
              />
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchRules(selectedSG)}
                loading={rulesLoading}
              >
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setAddModalOpen(true)}
              >
                Add Rule
              </Button>
            </Space>
          }
        >
          <Table
            dataSource={filteredRules}
            columns={columns}
            rowKey="rule_id"
            loading={rulesLoading}
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => `${total} rules`,
            }}
            size="small"
            locale={{ emptyText: 'No rules found for this security group.' }}
          />
        </Card>
      )}

      {/* ── Add Rule Modal ──────────────────────────────────────────────── */}
      <Modal
        title="Add Inbound Rule"
        open={addModalOpen}
        onCancel={() => {
          setAddModalOpen(false);
          addForm.resetFields();
          setUseTemplate(false);
          setTmplInc(''); setTmplApp(''); setTmplUser('');
        }}
        onOk={() => addForm.submit()}
        confirmLoading={addLoading}
        okText="Add Rule"
        destroyOnClose
        width={560}
      >
        {(() => {
          const remaining = Math.max(0, MAX_SG_RULES - rules.length);
          return (
            <Form
              form={addForm}
              layout="vertical"
              onFinish={handleAddRule}
              initialValues={{ protocol: 'tcp', cidrs: [''] }}
              style={{ marginTop: 16 }}
            >
              {/* ── CIDR list ─────────────────────────────────────────── */}
              <Form.List name="cidrs">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <Form.Item
                        key={field.key}
                        label={index === 0 ? 'CIDR / IP Address' : ''}
                        required={index === 0}
                        style={{ marginBottom: 8 }}
                      >
                        <Space.Compact style={{ width: '100%' }}>
                          <Form.Item
                            {...field}
                            noStyle
                            rules={[
                              { required: true, message: 'CIDR is required' },
                              {
                                validator: (_, value) =>
                                  isValidCIDR(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Invalid CIDR — e.g. 203.0.113.5/32')),
                              },
                            ]}
                          >
                            <Input
                              placeholder="203.0.113.5/32"
                              style={{ fontFamily: 'monospace', flex: 1 }}
                            />
                          </Form.Item>
                          {fields.length > 1 && (
                            <Button
                              icon={<MinusCircleOutlined />}
                              onClick={() => remove(field.name)}
                              danger
                            />
                          )}
                        </Space.Compact>
                      </Form.Item>
                    ))}
                    <Space style={{ marginBottom: 16 }} align="center">
                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => add('')}
                        disabled={fields.length >= remaining}
                      >
                        Add another IP
                      </Button>
                      {remaining < MAX_SG_RULES && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {remaining} slot{remaining !== 1 ? 's' : ''} remaining in this SG
                        </Text>
                      )}
                    </Space>
                    {remaining === 0 && (
                      <Alert
                        type="warning"
                        showIcon
                        message={`This SG is at the ${MAX_SG_RULES}-rule limit. Delete a rule before adding more.`}
                        style={{ marginBottom: 16 }}
                      />
                    )}
                  </>
                )}
              </Form.List>

              <Divider style={{ margin: '4px 0 16px' }} />

              {/* ── Protocol & Ports ──────────────────────────────────── */}
              <Form.Item
                name="protocol"
                label="Protocol"
                rules={[{ required: true, message: 'Protocol is required' }]}
              >
                <Select>
                  {PROTOCOL_OPTIONS.map((p) => (
                    <Option key={p.value} value={p.value}>{p.label}</Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prev, cur) => prev.protocol !== cur.protocol}
              >
                {({ getFieldValue }) =>
                  getFieldValue('protocol') !== '-1' && (
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="from_port"
                          label="From Port"
                          rules={[{ required: true, message: 'Required' }]}
                        >
                          <Input type="number" min={0} max={65535} placeholder="443" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="to_port"
                          label="To Port"
                          rules={[{ required: true, message: 'Required' }]}
                        >
                          <Input type="number" min={0} max={65535} placeholder="443" />
                        </Form.Item>
                      </Col>
                    </Row>
                  )
                }
              </Form.Item>

              {/* ── Description / Template ────────────────────────────── */}
              <Divider style={{ margin: '4px 0 16px' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Text strong>Description</Text>
                <Switch
                  size="small"
                  checked={useTemplate}
                  onChange={(checked) => {
                    setUseTemplate(checked);
                    if (!checked) { setTmplInc(''); setTmplApp(''); setTmplUser(''); }
                  }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {useTemplate ? 'Template on' : 'Use template'}
                </Text>
              </div>

              {useTemplate ? (
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  <Row gutter={12}>
                    <Col span={8}>
                      <Form.Item label="INC #" style={{ marginBottom: 8 }}>
                        <Input
                          placeholder="INC12345"
                          value={tmplInc}
                          onChange={(e) => setTmplInc(e.target.value)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Application" style={{ marginBottom: 8 }}>
                        <Input
                          placeholder="Vault"
                          value={tmplApp}
                          onChange={(e) => setTmplApp(e.target.value)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="User" style={{ marginBottom: 8 }}>
                        <Input
                          placeholder="jsmith"
                          value={tmplUser}
                          onChange={(e) => setTmplUser(e.target.value)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                    Preview: {tmplInc || 'INC#'} - {tmplApp || '<application>'} - {tmplUser || '<user>'} - {todayYYYYMMDD()}
                  </Text>
                </Space>
              ) : (
                <Form.Item name="description" style={{ marginBottom: 0 }}>
                  <Input
                    placeholder="e.g. GitLab CI runner — Corp office VPN"
                    maxLength={255}
                    showCount
                  />
                </Form.Item>
              )}
            </Form>
          );
        })()}
      </Modal>

      {/* ── Edit Description Modal ──────────────────────────────────────── */}
      <Modal
        title="Edit Rule Description"
        open={editModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          setEditingRule(null);
        }}
        onOk={() => editForm.submit()}
        confirmLoading={editLoading}
        okText="Save"
        destroyOnClose
      >
        {editingRule && (
          <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
            <Text type="secondary">
              Editing description for rule:{' '}
              <Text code>{editingRule.cidr_ip}</Text>{' '}
              ({editingRule.protocol === '-1' ? 'All Traffic' : (editingRule.protocol ?? '').toUpperCase()})
            </Text>
            <Form form={editForm} layout="vertical" onFinish={handleEditDescription}>
              <Form.Item
                name="description"
                label="New Description"
                rules={[{ required: true, message: 'Description cannot be empty' }]}
              >
                <Input placeholder="Enter a description..." maxLength={255} showCount />
              </Form.Item>
            </Form>
          </Space>
        )}
      </Modal>

    </Space>
  );
};

export default SecurityGroups;
